import React, {createRef, forwardRef, memo, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import ChatDrawerMessage from "@iso/components/ChatDrawer/ChatDrawerMessage";
import {fetchChatMessages} from "@api/chat";
import {flushSync} from "react-dom";
import Loading from "@iso/components/Loading/Loading";
import {CONVERSATION_ACTIVE_STATUS} from "@iso/components/ChatDrawer/ChatDrawer";
import {SET_CONVERSATION_CONTENT_LOADING} from "@iso/redux/chat/actions";
import { REACT_APP_CLOUD_FRONT } from "@iso/config/config";

const uniqueMessage = new Set();
const conversationAvatars = new Map();
const RELOAD_MILLISECOND = 5000;
const intervalFetchMessages = {
  current: null
};

const USER_ROLE = {
  AGENCY: 'agency',
  EMPLOYER: 'employer'
}

const ChatDrawerDetail = (props, chatBoxRef) => {
  const dispatch = useDispatch();
  const { selected, refetchMessage, isLoadContent, isLoading } = useSelector(state => state.chat);
  const { me } = useSelector(state => state.user);
  const [page, setPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [messages, setMessages] = useState([]);

  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const reset = () => {
    if (intervalFetchMessages.current) clearInterval(intervalFetchMessages.current)
    setPage(1)
    setMessages([])
    uniqueMessage.clear()
    conversationAvatars.clear()
    setIsFirstLoading(true)
    setIsLoadingMore(false)
    intervalFetchMessages.current = null
  }

  const isOwner = (userCode) => {
    return me?.code === userCode;
  };

  const getAvatar = (userCode) => {
    if (!userCode) return null;
    if (conversationAvatars.has(userCode))
      return `${REACT_APP_CLOUD_FRONT}/${conversationAvatars.get(userCode)}`;

    return null;
  };

  const handleScroll = (e) => {
    if (e.currentTarget.scrollTop === 0) {
      setIsLoadingMore(true);
      setPage(page + 1);
    }
  }

  const fetchChats = () => {
    fetchChatMessages({
      page: 1,
      recordPerPage,
      conversationId: selected._id
    }).then((resp) => {
      const avatars = resp?.data?.avatars;

      if (conversationAvatars.size <= 0 && avatars && avatars.length > 0) {
        avatars.map((avatar) => {
          if (avatar?.photos && avatar.photos.length > 0) {
            conversationAvatars.set(
              avatar._id,
              avatar.photos[avatar.photos.length - 1]?.path?.origin
            );
          }
        });
      }

      const data = resp?.data?.data;
      const isPositionBottomScroll = chatBoxRef?.current &&
        chatBoxRef?.current.scrollHeight - chatBoxRef?.current.scrollTop === chatBoxRef?.current.clientHeight;

      if (data && data.length > 0) {
        const newMessage = data.filter((message) => {
          if (uniqueMessage.has(message._id)) {
            return false;
          }

          uniqueMessage.add(message._id);
          return true;
        });

        if (newMessage.length > 0) {
          flushSync(() => {
            setMessages((msg) => {
              return [...msg, ...newMessage.reverse()]
            });
          })

          if (isPositionBottomScroll && chatBoxRef?.current) {
            chatBoxRef.current.scrollTo(0, chatBoxRef.current.scrollHeight);
          }
        }
      }
    }).then(resp => {
      if (isLoadContent) {
        dispatch({
          type: SET_CONVERSATION_CONTENT_LOADING,
          payload: false
        })
      }
    })
  }

  useEffect(() => {
    reset()
  }, [selected])

  useEffect(() => {
    if (intervalFetchMessages?.current) return;
    fetchChats()
    intervalFetchMessages.current = setInterval(fetchChats, RELOAD_MILLISECOND);
    setIsFirstLoading(false)
  }, [intervalFetchMessages.current])

  useEffect(() => {
    fetchChatMessages({
      page: page,
      recordPerPage,
      conversationId: selected._id
    }).then((resp) => {
      const data = resp?.data?.data;

      if (data && data.length > 0) {
        const newMessage = data.filter((message) => {
          if (uniqueMessage.has(message._id)) {
            return false;
          }

          uniqueMessage.add(message._id);
          return true;
        });

        if (newMessage.length > 0) {
          flushSync(() => {
            setMessages((msg) => {
              return [...newMessage.reverse(), ...msg]
            });
          })
        }

        if (newMessage.length > 0 && chatBoxRef?.current) {
          chatBoxRef.current.scrollTo(0, chatBoxRef.current.clientHeight);
        }

        if (newMessage.length < 0 && chatBoxRef?.current) {
          chatBoxRef.current.scrollTo(0, 1);
        }
      }

      if (!data || data.length <= 0) {
        setPage((page) => page - 1)
        chatBoxRef?.current && chatBoxRef.current.scrollTo(0, 1);
      }

      setIsLoadingMore(false)
    })
  }, [page, recordPerPage])

  useEffect(() => {
    if (!isFirstLoading) {
      fetchChats()
    }
  }, [refetchMessage])

  useEffect(() => {
    if (messages && chatBoxRef?.current) {
      chatBoxRef.current.scrollTo(0, chatBoxRef?.current.scrollHeight);
    }
  }, [isLoadContent])

  useEffect(() => {
    return () => {
      if (intervalFetchMessages.current) {
        clearInterval(intervalFetchMessages.current)
      }
    }
  }, [])

  if (isLoadContent) {
    return (
      <div className='flex flex-col chat-message'>
        <div className='flex flex-col overflow-x-auto h-30 chat-conversation' onScroll={handleScroll} ref={chatBoxRef}>
          <div className='flex justify-center'><Loading/></div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col chat-message'>
      <div className='flex flex-col overflow-x-auto h-30 chat-conversation' onScroll={handleScroll} ref={chatBoxRef}>
        {
          isLoadingMore && (
            <div className='flex justify-center'><Loading/></div>
          )
        }
        {
          messages && messages.map((message, index) => (
            <ChatDrawerMessage
              key={index}
              message={message?.content}
              createdAt={message?.created}
              isOwner={isOwner(message?.createBy?.code)}
              user={{
                ...message?.createBy,
                avatar: getAvatar(
                  [USER_ROLE.AGENCY, USER_ROLE.EMPLOYER].includes(message?.createBy?.userRole)
                    ? message?.createBy?.companyCode
                    : message?.createBy?.code,
                  )
              }}
            />
          ))
        }
        {
          selected && selected?.status !== CONVERSATION_ACTIVE_STATUS && (
            <div className='px-4 py-3'>
              <div className="separator text-xs">The conversation is closed!</div>
            </div>
          )
        }
      </div>
    </div>
  )
}

export default memo(forwardRef(ChatDrawerDetail));
